import React, { useState, useEffect, useRef } from "react";
import { navigate } from "gatsby"
import Flink from '~/components/shared/flink'
import { useSearchContentQuery } from "~/hooks/useSearchContentQuery"
import useKeyboardEvent from '~/utils/keyboard-evt'
import useSearch from '~/utils/use-search'
import { useTheme } from '~/contexts/ThemeContext'
import useAnalytics from '~/utils/analytics'
import { imgSize, isStag, jnstyls, compact } from '~/utils/helpers'

import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { InstantSearch, Configure } from 'react-instantsearch'
import { SearchBox, InfiniteHits, useInstantSearch } from 'react-instantsearch'

import IconClose from '~/components/icons/close'
import IconSearch from '~/components/icons/search'

import { aria_btn, solid_btn } from "~/styles/app.module.scss"
import * as tstyles from "~/styles/pdp/title.module.scss"
import * as styles from "~/styles/panels/search.module.scss"



const Search = () => {
  const { isMobile, setFreeze, setSearch, analytics, setAnalytics, setBread, gender, setGender } = useTheme()
  const content = useSearchContentQuery()
  const { setupFuse, buildVals, searchProds, saveSearch } = useSearch({
    synonyms: content.synonyms || [],
    boost: content.boost || []
  })
  const { ga_search_click, elev_search, elev_select } = useAnalytics()

  const all_calls = content.callouts || []
  let initgend = gender?.srGender?.slice(0,1) || null
  const init_pl = (typeof document !== 'undefined') && !!sessionStorage.getItem('searchpls')

  const [mobiHeight, setMobiHeight] = useState(false)
  const [searchData, setSearchData] = useState(null)
  const [prodResults, setProdResults] = useState([])
  const [searchType, setSearchType] = useState(initgend)
  const [fuseIndex, setFuseIndex] = useState(null)
  const [currSort, setCurrSort] = useState('best')
  const [groupPl, setGroupPl] = useState(init_pl)

  const { closeListen } = useKeyboardEvent()

  const panelRef = useRef(null)
  const inptRef = useRef(null)
  const resultsRef = useRef(null)
  const searchTimeout = useRef(null)

  const handleClose = () => {
    const panel_el = panelRef.current

    if (!panel_el) return
    panel_el.setAttribute('aria-hidden', 'true')
    setTimeout(() => {
      setFreeze(false)
      setSearch(false)
    }, 200)
  }

  const searchClear = () => {
    setProdResults([])
    setCurrSort('best')
    localStorage.removeItem('search')
    inptRef.current.value = ''
  }

  const debounce = (func, delay) => {
    return (...args) => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
      searchTimeout.current = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const srchtest = (typeof document !== 'undefined') && sessionStorage.getItem('nsearch')
  const searchClient = srchtest ? algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_API) : null

  const handleSearch = () => {
    if (!searchData) return

    const val = inptRef.current.value.toLowerCase()

    if ( val === '' ) {
      searchClear()
      return
    }

    if ( val.length < 3 ) return

    let val_arr = buildVals(val, fuseIndex, searchType, setSearchType)
    const prods = searchProds({
      val,
      val_arr, 
      products: searchData.products, 
      searchType, 
      currSort,
      fuseIndex,
      isPanel: true
    })
    setProdResults(prods)
    do_saving()
    const anaprods = sessionStorage.getItem('searchpls') ? prods.flatMap(x => x.feats) : prods.flat()
    debounce(elev_search, 2000)(val, anaprods.slice(0,36))
  }

  const gaClick = (product=false, isPopular=false, index=false) => {
    const val = inptRef.current.value

    const term = isPopular ? isPopular.toLowerCase() : inptRef.current.value
    const type = searchType === null ? 'all' : searchType === 'm' ? 'men' : searchType === 'w' ? 'women' : searchType

    ga_search_click(term, type, !!isPopular)
    // elev_search(val, prodResults.flat())
    if (product) {
      setAnalytics({...analytics, list: 'search results'})
      elev_select(product, index, 'search results')
    }
  }

  const get_search_data = () => {
    const url = isStag() 
      ? 'https://cdn-staging.buckmason.com/search/search-json.json' 
      : 'https://cdn.buckmason.com/search/search-json.json'
    fetch(url)
      .then(res => res.json())
      .then(data => setSearchData(data)) 
  }

  const do_saving = async () => {
    const val = inptRef.current.value
    if ( val === '' ) return

    const prods = sessionStorage.getItem('searchpls') ? prodResults.flatMap(x => x.feats) : prodResults.flat()

    const obj = {
      prodResults: prods,
      searchType: searchType,
      currSort
    }

    const vals_arr = compact(val.split(' '))
    const has_saved = await saveSearch(obj, vals_arr)
    return has_saved
  }

  const goto_page = async () => {
    const has_saved = await do_saving()
    if (has_saved) {
      if (window.location.pathname.includes('/search')) {
        window.location = `/search`
        handleClose()
      } else {
        navigate('/search')
        handleClose()
      }
    }
  }

  const check_enter = (e) => {
    if (e.key !== 'Enter' || !searchData) return
    goto_page()
  }

  const checkSize = (e) => {
    if (!isMobile) {
      if (!!mobiHeight) setMobiHeight(null)
      return
    }
    const max = `${window.visualViewport.height}px`
    setMobiHeight(max)
  }

  const countText = () => {
    let num
    if (sessionStorage.getItem('searchpls')) {
      num = prodResults.flatMap(x => x.feats).length
    } else {
      num = prodResults.flat().length
    }
    return `${num} result${num !== 1 ? 's' : ''}`
  }

  const calloutText = (id) => {
    return all_calls.find(x => x.id === id)?.data || {}
  }

  const tagMatch = (matches, tag) => {
    return matches.includes(tag) || matches.find(x => tag.includes(x))
  }

  const pubString = (stamp) => {
    const pub = new Date(stamp * 1000)
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    return `${months[pub.getMonth()]} ${pub.getDate()} ${pub.getFullYear()}`
  }

  

  useEffect(() => {
    setBread({handle: 'search', title: 'Search Results'})

    closeListen(handleClose)

    if (!searchData) get_search_data()
    if (!prodResults) localStorage.removeItem('search')
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!inptRef.current) return
    setTimeout(() => {
      if (inptRef.current) inptRef.current.focus()
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inptRef])

  useEffect(() => {
    if (!searchData) return
    if (fuseIndex) return
    setupFuse(searchData.products, setFuseIndex)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData])

  useEffect(() => {
    if (!fuseIndex) return

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuseIndex])

  useEffect(() => {
    if (groupPl && !sessionStorage.getItem('searchpls')) {
      sessionStorage.setItem('searchpls','true')
    }
    if (!groupPl && !!sessionStorage.getItem('searchpls')) {
      sessionStorage.removeItem('searchpls')
    }
    resultsRef.current.scrollTo({
      top: 0,
      behavior: 'instant'
    })
    handleSearch()

    if (!!searchType && gender.srGender.slice(0,1) !== searchType) {
      const new_gend = searchType === 'm' ? 'men' : 'women'
      setGender(new_gend)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType, currSort, groupPl])
  
  useEffect(() => {
    setFreeze(true)
    const panel = panelRef.current
    setTimeout(() => {
      panel.setAttribute('aria-hidden', 'false')
    }, 100)
  }, [setFreeze])

  useEffect(() => {
    window.addEventListener('keydown', check_enter)
    window.visualViewport.addEventListener('resize', checkSize)

    return () => {
      window.removeEventListener('keydown', check_enter)
      window.visualViewport.removeEventListener('resize', checkSize)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  })


  return (
    <section 
      className={styles.searchwr} 
      aria-label="Search products" 
      role="dialog" 
      aria-modal="true" 
      aria-hidden="true"
      ref={panelRef}
      style={{maxHeight: mobiHeight ? mobiHeight : ''}}
    >
      <button 
        type="button"
        className={[aria_btn, styles.close].join(' ')}
        aria-label="Close sidebar" 
        onClick={(e)=> {handleClose()}}
      >
        <IconClose icon_classes={''} />
      </button>
      <div className={styles.head}>
        <div className={styles.inputwr}>
          <input 
            type="search"
            name="q"
            placeholder="Search our store"
            className={styles.search_inpt}
            onChange={(e)=>{handleSearch()}}
            ref={inptRef}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
          />
          {inptRef?.current?.value != '' && (
            <button
              type='button'
              className={styles.clear}
              onClick={(e) => searchClear()}
            >
              Clear
            </button>
          )}
          <div className={styles.search_btn}>
            <IconSearch icon_classes={''} />
          </div>
          <div className={styles.search_gender} >
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search all products" 
              aria-current={searchType === null ? 'true' : 'false'}
              onClick={(e)=>{setSearchType(null)}}
            >
              All
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search mens products" 
              aria-current={searchType === 'm' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('m')}}
            >
              Men's
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search womens products" 
              aria-current={searchType === 'w' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('w')}}
            >
              Women's
            </button>
          </div>
        </div>
      </div>
      <div className={styles.resultswr} style={{maxHeight: (prodResults.length > 0) ? '90dvh' : 0}}>
        <div 
          className={styles.results} 
          ref={resultsRef}
        >
          {(prodResults.length > 0) && (
            <div className={styles.restitle}>
              <div className={styles.title}>{countText()}</div>
              <div className={styles.count_sorts}>
                {srchtest && (
                  <div className={styles.sorts}>
                    <button
                      type='button'
                      className={styles.sort}
                      aria-current={(groupPl).toString()}
                      onClick={(e) => setGroupPl(!groupPl)}
                    >
                      Group by Pl
                    </button>
                    <button
                      type='button'
                      className={styles.sort}
                      aria-current={(currSort === 'best').toString()}
                      onClick={(e) => setCurrSort('best')}
                    >
                      Best Sellers
                    </button>
                    <button
                      type='button'
                      className={styles.sort}
                      aria-current={(currSort === 'recent').toString()}
                      onClick={(e) => setCurrSort('recent')}
                    >
                      Recent
                    </button>
                    <button
                      type='button'
                      className={styles.sort}
                      aria-current={(currSort === 'price').toString()}
                      onClick={(e) => setCurrSort('price')}
                    >
                      Price
                    </button>
                  </div>
                )}
                
              </div>
            </div>
          )}
          {(prodResults.length > 0 && !sessionStorage.getItem('searchpls')) && prodResults.map((group, index) => 
            <div key={`search_grp_${index}`} className={jnstyls([styles.group, srchtest ? styles.testgrp : ''])}>
              {srchtest && (
                <div className={styles.testtitle}>
                  {prodResults.length === 3 ? (
                    <>{index === 0 ? 'Boosts' : index === 1 ? 'More exact' : 'Fuzzy'}</>
                  ) : (
                    <>{index === 0 ? 'More exact' : 'Fuzzy'}</>
                  )}
                  <br/>
                </div>
              )}
              {group.map((result, pindex) => {
                let image = result.media.length > 0 ? imgSize(result.media[0],300) : false
                const is_gift = result.handle.includes('gift')
                const colors = result.pl_colors ? result.pl_colors : false

                let vpricMin = false
                let vpricMax = false

                if ( is_gift ) {
                  vpricMin = 25
                  vpricMax = 100
                }

                return (
                  <Flink 
                    key={result.handle} 
                    href={`/products/${result.handle}`} 
                    className={jnstyls([styles.item, srchtest ? styles.testitem : ''])} 
                    result=""
                    onClick={(e)=>{
                      handleClose();
                      gaClick(result, false, index)
                    }}
                  >
                    {image && (
                      <div className={styles.image}>
                        <img src={image} alt={result.title} className={styles.img} loading="lazy" width="400" height="532" />
                      </div>
                    )}
                    <div className={styles.info} style={{rowGap: '0.4em'}}>
                      <div className={styles.item_title}>
                        {(!searchType && searchType !== 'u') && (
                          <>
                            {result.gender === 'm' ? "Men's " : result.gender === 'w' ? "Women's " : ''}
                          </>
                        )}
                        {result.title}
                      </div>
                      <div className={styles.price}>
                        {is_gift ? (
                          <span>
                            ${vpricMin}–${vpricMax}
                          </span>
                        ) : result.percent_off ? (
                          <>
                            <span className={tstyles.price_sale}>
                              ({result.percent_off}% OFF)
                            </span>
                            <span className={tstyles.price_perc}>
                              ${result.first_product_price}
                            </span>
                            <span className={tstyles.price_full}>
                              ${result.first_full_price}
                            </span>
                          </>
                        ) : (
                          <>${result.first_product_price}</>
                        )}
                      </div>
                      <div className={jnstyls([styles.info, styles.infoextra])}>
                        <div className={styles.colors} style={{fontSize: '1rem'}}>
                          {colors && (
                            <>{colors} Other Color{colors > 1 ? 's' : ''}</>
                          )}
                        </div>
                        {(result.altcuts) && (
                          <div className={styles.alts} style={{textTransform: 'capitalize'}}>
                            <div className={styles.altlabel}>
                              {result.altcuts.os.length} Length{result.altcuts.os.length !== 1 ? 's' : ''}
                            </div>
                            {result.altcuts.os.map(item =>
                              <div key={`prod_${result.mid}_alt_${item}`} className={styles.altitem}>
                                {item}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {(result.callouts?.length > 0) && (
                        <div className={jnstyls([styles.info, styles.infoextra])} style={{marginTop: '0.4em'}}>
                          {result.callouts?.length > 0 && (
                            <div className={styles.callouts} style={{textTransform: 'capitalize'}}>
                              {result.callouts.map(id =>
                                <div 
                                  key={`prod_${result.mid}_call_${id}`}
                                  className={styles.callout} 
                                  style={{color: calloutText(id)?.color || ''}}
                                >
                                  {calloutText(id).name}
                                </div>
                              )}
                            </div>
                          )}
                          <div className={styles.alts}></div>
                        </div>
                      )}
                      {srchtest && (
                        <>
                          <div className={styles.data}>
                            <div className={styles.ditem}>
                              <span>Bests Index</span>
                              {result.best}
                            </div>
                            <div className={styles.ditem}>
                              <span>Broken?</span>
                              {result.broken?.toString() || '?'}
                            </div>
                            <div className={styles.ditem}>
                              <span>Exacts</span>
                              {result.hits}
                            </div>
                            <div className={styles.ditem}>
                              <span>Published</span>
                              {pubString(result.pub)}
                            </div>
                          </div>
                          <div className={styles.data}>
                            <div className={styles.ditem} style={{flex: '0 1 auto'}}>
                              <span>Fuse Hits</span>
                              <div className={styles.dtags}>
                                {result.matches.map((x, index) => 
                                  <div key={`ftag_${result.mid}_${x}_${index}`} className={jnstyls([styles.dtag])}>
                                    {x}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={styles.ditem}>
                              <span>Tags</span>
                              <div className={styles.dtags}>
                                {result.search.map((x,index) => 
                                  <div key={`ptag_${result.mid}_${x}_${index}`} className={jnstyls([styles.dtag, tagMatch(result.matches, x) ? styles.match : ''])}>
                                    {x}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Flink>
                )
              })}
            </div>
          )}
          {(prodResults.length > 0 && sessionStorage.getItem('searchpls')) && prodResults.map((product_line,index) => 
            <div key={`searchpl_${product_line.pl_id}`} className={jnstyls([styles.searchpl, srchtest ? styles.testpl : ''])}>
              <div className={styles.pltitle}>
                <div className={styles.testtitle}>
                  {(!searchType && searchType !== 'u') && (
                    <>
                      {product_line.gender === 'm' ? "Men's " : product_line.gender === 'w' ? "Women's " : ''}
                    </>
                  )}
                  {product_line.pl_name}
                </div>
                <div className={styles.plprice}>
                  ${product_line.price}
                </div>
              </div>
              {product_line.feats.map((result, pindex) => {
                let image = result.media.length > 0 ? imgSize(result.media[0],300) : false

                return (
                  <Flink 
                    key={result.handle} 
                    href={`/products/${result.handle}`} 
                    className={jnstyls([styles.item, srchtest ? styles.testitem : ''])} 
                    result=""
                    onClick={(e)=>{
                      handleClose();
                      gaClick(result, false, index)
                    }}
                  >
                    {image && (
                      <div className={styles.image}>
                        <img src={image} alt={result.title} className={styles.img} loading="lazy" width="400" height="532" />
                      </div>
                    )}
                    <div className={styles.info}>
                      <div className={styles.item_title} style={{textTransform: 'capitalize'}}>
                        {result.color}
                      </div>

                      {(result.callouts?.length > 0 || result.altcuts) && (
                        <div className={jnstyls([styles.info, styles.infoextra])}>
                          {result.callouts?.length > 0 && (
                            <div className={styles.callouts} style={{textTransform: 'capitalize'}}>
                              {result.callouts.map(id =>
                                <div 
                                  key={`prod_${result.mid}_call_${id}`} 
                                  className={styles.callout} 
                                  style={{color: calloutText(id)?.color || ''}}
                                >
                                  {calloutText(id).name}
                                </div>
                              )}
                            </div>
                          )}
                          {result.altcuts && (
                            <div className={styles.alts} style={{textTransform: 'capitalize'}}>
                              <div className={styles.altlabel}>
                                {result.altcuts.os.length} {result.altcuts.t}{result.altcuts.os.length !== 1 ? 's' : ''}
                              </div>
                              {result.altcuts.os.map(item =>
                                <div key={`prod_${result.mid}_alt_${item}`}className={styles.altitem}>
                                  {item}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    
                      {false && (
                        <>
                          <div className={styles.data}>
                            <div className={styles.ditem}>
                              <span>Bests Index</span>
                              {result.best}
                            </div>
                            <div className={styles.ditem}>
                              <span>Broken?</span>
                              {result.broken?.toString() || '?'}
                            </div>
                            <div className={styles.ditem}>
                              <span>Search Score</span>
                              {result.score}%
                            </div>
                            <div className={styles.ditem}>
                              <span>Exacts</span>
                              {result.hits}
                            </div>
                            <div className={styles.ditem}>
                              <span>Published</span>
                              {pubString(result.pub)}
                            </div>
                          </div>
                          <div className={styles.data}>
                            <div className={styles.ditem} style={{flex: '0 1 auto'}}>
                              <span>Fuse Hits</span>
                              <div className={styles.dtags}>
                                {result.matches.map((x, index) => 
                                  <div key={`ftag_${result.mid}_${x}_${index}`} className={jnstyls([styles.dtag])}>
                                    {x}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={styles.ditem}>
                              <span>Tags</span>
                              <div className={styles.dtags}>
                                {result.search.map((x,index) => 
                                  <div key={`ptag_${result.mid}_${x}_${index}`} className={jnstyls([styles.dtag, tagMatch(result.matches, x) ? styles.match : ''])}>
                                    {x}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Flink>
                )
              })}
              {product_line.products.length > 0 && (
                <PlOtherColors 
                  products={product_line.products}
                  index={index}
                  handleClose={handleClose}
                  gaClick={gaClick}
                />
              )}
            </div>
          )}
        </div>
        <div className={styles.foot}>
          <button type="button" className={`${solid_btn} ${styles.btn}`} onClick={(e)=>{goto_page()}}> 
            See All Results
          </button>
        </div>
      </div>
      <div className={styles.blind} style={{maxHeight: (prodResults.length > 0) ? '0' : ''}}>
        <div className={styles.popular}>
          {(!searchType || searchType === 'm') && content?.mens_links?.length > 0 && (
            <div className={styles.pop_group}>
              <div className={styles.title}>
                Popular Men's Searches
              </div>
              <ol className={styles.popular_list}>
                {content.mens_links.map(link => 
                  <li key={link.link_text} className={styles.popular_item}>
                    <Flink 
                      href={link.url} 
                      aria-label={`Popular Search: ${link.link_text}`}
                      onClick={(e)=>{
                        handleClose();
                        gaClick(false,link.link_text)
                      }} 
                    >
                      {link.link_text}
                    </Flink>
                  </li>
                )}
              </ol>
            </div>
          )}
          {(!searchType || searchType === 'w') && content?.womens_links?.length > 0 && (
            <div className={styles.pop_group}>
              <div className={styles.title}>
                Popular Women's Searches
              </div>
              <ol className={styles.popular_list}>
                {content.womens_links.map(link => 
                  <li key={link.link_text} className={styles.popular_item}>
                    <Flink 
                      href={link.url} 
                      aria-label={`Popular Search: ${link.link_text}`}
                      onClick={(e)=>{
                        handleClose();
                        gaClick(false,link.link_text)
                      }} 
                    >
                      {link.link_text}
                    </Flink>
                  </li>
                )}
              </ol>
            </div>
          )}
        </div>
      </div>

    </section>
  )
}


const PlOtherColors = ({ products, index, handleClose, gaClick}) => {
  const [currColor, setCurrColor] = useState(null)

  return (
    <div className={styles.plcolorswr}>
      <div className={styles.testtitle}>
        {currColor ? 
          currColor
        : `And ${products.length} other Color${products.length > 1 ? 's' : ''}`
        }
      </div>
      <div className={styles.plcolors}>
        {products.slice(0,8).map((result, pindex) => {
          let image = result.media.length > 0 ? imgSize(result.media[0],300) : false

          return (
            <Flink 
              key={result.handle} 
              href={`/products/${result.handle}`} 
              className={jnstyls([styles.plcolor])} 
              result=""
              onClick={(e)=>{
                handleClose();
                gaClick(result, false, index)
              }}
              onMouseEnter={(e)=>setCurrColor(result.color)} 
              onMouseLeave={(e)=>setCurrColor(null)} 
            >
              {image && (
                <div className={styles.image}>
                  <img src={image} alt={result.title} className={styles.img} loading="lazy" width="400" height="532" />
                </div>
              )}
            </Flink>
          )
        })}
      </div>
    </div>
  )
}




export default Search
